import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  fetchChangeHistory,
  type ChangeHistoryResponse,
  type ChangeEvent,
} from '../../services/analytics.service';
import { usePagesList } from '../../hooks/usePagesList';
import { useSiteStore } from '../../store/site.store';

// Change type badge colors
const changeTypeColors: Record<string, 'error' | 'warning' | 'info' | 'success' | 'default'> = {
  META_CHANGE: 'info',
  CANONICAL_CHANGE: 'warning',
  INDEXABILITY_CHANGE: 'error',
  CONTENT_CHANGE: 'success',
  TEMPLATE_CHANGE: 'default',
  SCHEMA_CHANGE: 'secondary',
};

// Change type labels
const changeTypeLabels: Record<string, string> = {
  META_CHANGE: 'Meta Tags',
  CANONICAL_CHANGE: 'Canonical',
  INDEXABILITY_CHANGE: 'Indexability',
  CONTENT_CHANGE: 'Content',
  TEMPLATE_CHANGE: 'Template',
  SCHEMA_CHANGE: 'Schema',
};

export default function ChangeDetectionAnalyticsPage() {
  // Get site and pages from cache
  const { selectedSite } = useSiteStore();
  const { pages, loading: pagesLoading } = usePagesList();
  
  // History state
  const [pageId, setPageId] = useState<number | ''>('');
  const [changeType, setChangeType] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<ChangeHistoryResponse | null>(null);
  const debounceTimer = useRef<number | null>(null);

  const loadHistory = async () => {
    if (!selectedSite?.tenantId) {
      setError('Please select a site first');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const selectedPage = pageId ? pages.find(p => p.id === pageId) : null;
      const res = await fetchChangeHistory({
        tenant_id: selectedSite.tenantId,
        page_url: selectedPage?.url || undefined,
        change_type: changeType || undefined,
        page: page,
        page_size: 20,
      });
      setHistory(res.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load change history');
    } finally {
      setLoading(false);
    }
  };

  const trigger = () => {
    if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
    debounceTimer.current = window.setTimeout(() => {
      loadHistory();
    }, 400);
  };

  useEffect(() => {
    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite?.tenantId, pageId, changeType, page]);

  // View changes modal state
  const [viewChangeOpen, setViewChangeOpen] = useState(false);
  const [selectedChange, setSelectedChange] = useState<ChangeEvent | null>(null);

  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const handleViewChange = (change: ChangeEvent) => {
    setSelectedChange(change);
    setViewChangeOpen(true);
  };

  const handleCloseViewChange = () => {
    setViewChangeOpen(false);
    setSelectedChange(null);
  };

  const renderWebhookStatus = (change: ChangeEvent) => {
    if (change.webhook_sent === 1) {
      return (
        <Tooltip title={`Webhook sent ${change.webhook_sent_at ? 'at ' + new Date(change.webhook_sent_at).toLocaleString() : ''}`}>
          <Chip
            icon={<CheckCircleIcon />}
            label={`Sent (${change.webhook_response_code || 'N/A'})`}
            color="success"
            size="small"
            variant="outlined"
          />
        </Tooltip>
      );
    }
    return (
      <Tooltip title="Webhook not sent">
        <Chip
          icon={<ScheduleIcon />}
          label="Not Sent"
          color="default"
          size="small"
          variant="outlined"
        />
      </Tooltip>
    );
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5">Change Detection</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => loadHistory()} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Enhanced Filters */}
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <FilterListIcon color="action" />
            <Typography variant="subtitle2" color="text.secondary">
              Filters
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 400, flexGrow: 1 }}>
              <InputLabel>Page (optional)</InputLabel>
              <Select
                value={pageId}
                onChange={(e: SelectChangeEvent) => setPageId(e.target.value ? Number(e.target.value) : '')}
                label="Page (optional)"
                disabled={pagesLoading || !selectedSite}
              >
                <MenuItem value="">All Pages</MenuItem>
                {pages && pages.length > 0 ? (
                  pages.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.url}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No pages available</MenuItem>
                )}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Change Type</InputLabel>
              <Select
                value={changeType}
                onChange={(e: SelectChangeEvent) => setChangeType(e.target.value)}
                label="Change Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="META_CHANGE">🏷️ Meta Tags</MenuItem>
                <MenuItem value="CANONICAL_CHANGE">🔗 Canonical</MenuItem>
                <MenuItem value="INDEXABILITY_CHANGE">🚫 Indexability</MenuItem>
                <MenuItem value="CONTENT_CHANGE">📄 Content</MenuItem>
                <MenuItem value="TEMPLATE_CHANGE">🎨 Template</MenuItem>
                <MenuItem value="SCHEMA_CHANGE">📐 Schema</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              onClick={() => {
                setPageId('');
                setChangeType('');
                setPage(1);
              }}
            >
              Clear
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Enhanced History List */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Recent Changes
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading && (
            <Stack direction="row" alignItems="center" spacing={1} justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
              <Typography>Loading changes...</Typography>
            </Stack>
          )}

          {!loading && (!history?.items || history.items.length === 0) && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No changes found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your filters or check back later
              </Typography>
            </Box>
          )}

          {!loading && history?.items && history.items.length > 0 && (
            <Stack spacing={2}>
              {history.items.map((evt: ChangeEvent, idx: number) => (
                <Card
                  key={evt.id ?? idx}
                  variant="outlined"
                  sx={{
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 2 },
                  }}
                >
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                      <Stack spacing={1} flex={1}>
                        {/* Change Type Badge */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Chip
                            label={changeTypeLabels[evt.change_type] || evt.change_type}
                            color={changeTypeColors[evt.change_type] || 'default'}
                            size="small"
                          />
                          {renderWebhookStatus(evt)}
                        </Stack>

                        {/* Page URL */}
                        <Typography
                          variant="body2"
                          color="primary"
                          sx={{
                            wordBreak: 'break-all',
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                          }}
                        >
                          {evt.page_id}
                        </Typography>

                        {/* Timestamp */}
                        <Typography variant="caption" color="text.secondary">
                          Detected {formatRelativeTime(evt.detected_at)} 
                          {' • '}
                          {new Date(evt.detected_at).toLocaleString()}
                        </Typography>

                        {/* Change Hash (technical detail) */}
                        {evt.change_hash && (
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          >
                            Hash: {evt.change_hash.substring(0, 16)}...
                          </Typography>
                        )}
                      </Stack>

                      {/* Actions */}
                      <Stack direction="row" spacing={1}>
                        <Button 
                          size="small" 
                          variant="outlined"
                          onClick={() => handleViewChange(evt)}
                        >
                          View Changes
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}

              {/* Pagination */}
              <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" mt={2}>
                <Button
                  disabled={!history.pagination?.has_prev}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  variant="outlined"
                  size="small"
                >
                  Previous
                </Button>
                <Typography variant="body2">
                  Page {history.pagination?.page} of {history.pagination?.total_pages}
                  {' • '}
                  {history.pagination?.total_items} total
                </Typography>
                <Button
                  disabled={!history.pagination?.has_next}
                  onClick={() => setPage((p) => p + 1)}
                  variant="outlined"
                  size="small"
                >
                  Next
                </Button>
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>



      {/* View Change Details Modal */}
      <Dialog
        open={viewChangeOpen}
        onClose={handleCloseViewChange}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Change Details</Typography>
            <IconButton onClick={handleCloseViewChange} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedChange && (
            <Stack spacing={2}>
              {/* Change Type */}
              <Box>
                <Typography variant="caption" color="text.secondary">Change Type</Typography>
                <Box mt={0.5}>
                  <Chip
                    label={changeTypeLabels[selectedChange.change_type] || selectedChange.change_type}
                    color={changeTypeColors[selectedChange.change_type] || 'default'}
                    size="small"
                  />
                </Box>
              </Box>

              {/* Page URL */}
              <Box>
                <Typography variant="caption" color="text.secondary">Page URL</Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', mt: 0.5, wordBreak: 'break-all' }}
                >
                  {selectedChange.page_id}
                </Typography>
              </Box>

              {/* Detected Time */}
              <Box>
                <Typography variant="caption" color="text.secondary">Detected</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {formatRelativeTime(selectedChange.detected_at)}
                  {' • '}
                  {new Date(selectedChange.detected_at).toLocaleString()}
                </Typography>
              </Box>

              {/* Webhook Status */}
              <Box>
                <Typography variant="caption" color="text.secondary">Webhook Status</Typography>
                <Box mt={0.5}>
                  {renderWebhookStatus(selectedChange)}
                </Box>
                {selectedChange.webhook_sent === 1 && selectedChange.webhook_sent_at && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Sent at: {new Date(selectedChange.webhook_sent_at).toLocaleString()}
                  </Typography>
                )}
              </Box>

              {/* Change Hash */}
              {selectedChange.change_hash && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Change Hash</Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mt: 0.5, wordBreak: 'break-all' }}
                  >
                    {selectedChange.change_hash}
                  </Typography>
                </Box>
              )}

              {/* Additional Info */}
              <Box>
                <Typography variant="caption" color="text.secondary">Additional Information</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" color="text.secondary">
                    This change was detected by the automated change detection system.
                    The change hash is used for deduplication within a 7-day window.
                  </Typography>
                </Paper>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewChange}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
